import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, map, catchError, throwError, of } from 'rxjs';
import { BYPASS_ERROR_INTERCEPTOR } from '../interceptors/error.interceptor';
import { SUPPRESS_SUCCESS_TOAST } from '../interceptors/success-toast.interceptor';
import { User, UserRole } from '../models/user.interface';
import { LoginCredentials, LoginResponse, RefreshTokenRequest } from '../models/auth.interface';
import { TokenService } from './token.service';
import { API_ENDPOINTS } from '../constants/api.constants';
import { APP_ROUTES } from '../constants/routes.constants';

/** Réponse de confirmation de réinitialisation : message + session éventuelle */
export interface ResetPasswordConfirmResponse {
  message?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  success?: boolean;
}

/** Réponse de changement de mot de passe */
export interface ChangePasswordResponse {
  success?: boolean;
  message?: string;
  data?: {
    accessToken?: string;
    refreshToken?: string;
    user?: User;
  };
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);

  private readonly USER_KEY = 'crm_lanala_user';

  private readonly currentUserSignal = signal<User | null>(this.getStoredUser());

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.currentUserSignal());
  readonly permissions = computed(() => this.currentUserSignal()?.permissions ?? []);
  readonly userRole = computed(() => {
    const roles = this.currentUserSignal()?.roles ?? [];
    if (roles.includes(UserRole.ADMIN)) return UserRole.ADMIN;
    if (roles.includes(UserRole.SUPERVISEUR)) return UserRole.SUPERVISEUR;
    if (roles.includes(UserRole.BACK_OFFICE)) return UserRole.BACK_OFFICE;
    if (roles.includes(UserRole.TELECONSEILLER)) return UserRole.TELECONSEILLER;
    return null;
  });

  private getStoredUser(): User | null {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(this.USER_KEY);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse user from localStorage', e);
        }
      }
    }
    return null;
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials).pipe(
      tap((res) => {
        this.tokenService.setAccessToken(res.accessToken);
        this.tokenService.setRefreshToken(res.refreshToken);
        this.setCurrentUser(res.user);
      }),
      catchError((err) => {
        console.error('Login error', err);
        return throwError(() => err);
      }),
    );
  }

  refresh(refreshToken: string): Observable<LoginResponse> {
    const req: RefreshTokenRequest = { refreshToken };
    return this.http.post<LoginResponse>(API_ENDPOINTS.AUTH.REFRESH, req, {
      context: new HttpContext().set(BYPASS_ERROR_INTERCEPTOR, true),
    }).pipe(
      tap((res) => {
        this.tokenService.setAccessToken(res.accessToken);
        if (res.refreshToken) {
          this.tokenService.setRefreshToken(res.refreshToken);
        }
        this.setCurrentUser(res.user);
      }),
      catchError((err) => {
        this.logout();
        return throwError(() => err);
      }),
    );
  }

  /** Demande d'envoi du lien de réinitialisation au mail fourni */
  requestPasswordReset(email: string, context?: HttpContext): Observable<{ success: boolean; message: string; timestamp: string }> {
    const merged = new HttpContext()
      .set(BYPASS_ERROR_INTERCEPTOR, true)
      .set(SUPPRESS_SUCCESS_TOAST, context?.get(SUPPRESS_SUCCESS_TOAST) ?? false);
    return this.http.post<{ success: boolean; message: string; timestamp: string }>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      { email },
      { context: merged },
    ).pipe(
      catchError((err) => {
        console.error('Reset password request error', err);
        return throwError(() => err);
      }),
    );
  }

  /** Confirme la réinitialisation via token reçu par email et connecte immédiatement si la réponse contient un token */
  confirmResetPassword(token: string, nouveauMotDePasse: string, context?: HttpContext): Observable<ResetPasswordConfirmResponse> {
    const body = { token, nouveauMotDePasse };
    return this.http.post<ResetPasswordConfirmResponse>(API_ENDPOINTS.AUTH.RESET_PASSWORD_CONFIRM, body, { context }).pipe(
      tap((res) => {
        if (res?.accessToken) {
          this.tokenService.setAccessToken(res.accessToken);
          if (res.refreshToken) {
            this.tokenService.setRefreshToken(res.refreshToken);
          }
          if (res.user) {
            this.setCurrentUser(res.user);
          }
        }
      }),
      catchError((err) => {
        console.error('Confirm reset password error', err);
        return throwError(() => err);
      }),
    );
  }

  /** Change le mot de passe pour l'utilisateur connecté. Si la réponse contient un nouveau accessToken, on met à jour la session. */
  changePassword(motDePasseActuel: string, nouveauMotDePasse: string, context?: HttpContext): Observable<ChangePasswordResponse> {
    const body = { motDePasseActuel, nouveauMotDePasse };
    return this.http.put<ChangePasswordResponse>(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, body, { context }).pipe(
      tap((res) => {
        if (res?.success && res?.data?.accessToken) {
          this.tokenService.setAccessToken(res.data.accessToken);
          if (res.data.refreshToken) {
            this.tokenService.setRefreshToken(res.data.refreshToken);
          }
          if (res.data.user) {
            this.setCurrentUser(res.data.user);
          }
        }
      }),
      catchError((err) => {
        console.error('Change password error', err);
        return throwError(() => err);
      }),
    );
  }

  logout(): void {
    this.tokenService.clearTokens();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.USER_KEY);
    }
    this.currentUserSignal.set(null);
    this.router.navigate([APP_ROUTES.LOGIN]);
  }

  setCurrentUser(user: User): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
    this.currentUserSignal.set(user);
  }

  tryRestoreSession(): Observable<boolean> {
    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      this.currentUserSignal.set(null);
      return of(false);
    }
    return this.http.post<LoginResponse>(API_ENDPOINTS.AUTH.REFRESH, { refreshToken }, {
      context: new HttpContext().set(BYPASS_ERROR_INTERCEPTOR, true),
    }).pipe(
      tap((res) => {
        this.tokenService.setAccessToken(res.accessToken);
        if (res.refreshToken) {
          this.tokenService.setRefreshToken(res.refreshToken);
        }
        this.setCurrentUser(res.user);
      }),
      map(() => true),
      catchError(() => {
        this.tokenService.clearTokens();
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(this.USER_KEY);
        }
        this.currentUserSignal.set(null);
        return of(false);
      }),
    );
  }
}
